import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhoneVolume, faChevronDown, faEnvelope, faXmark,
  faLocationDot, faBars, faSignInAlt, faKey
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './Header.css';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';

// One place to edit the menu (desktop + mobile both read from here)
const NAV = [
  { label: 'Home', to: '/' },
  {
    label: 'Courses',
    key: 'courses',
    items: [
      { label: 'Our Programs', to: '/Programs' },
      { label: 'Curriculum Overview', to: '/curriculum' },
      { label: 'Pricing Plans', to: '/PricingPlans' },
    ],
  },
  {
    label: 'Activities',
    key: 'activities',
    items: [
      { label: 'Robotics Activities', to: '/wedo20' },
      { label: 'Events', to: '/Events' },
    ],
  },
  { label: 'Student Achievements', to: '/studentachievements' },
  { label: 'About Us', to: '/AboutUs' },
];

const Header = () => {
  const [offer, setOffer] = useState(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const leaveTimer = useRef(null);
  const location = useLocation();
  const EMAIL = process.env.REACT_APP_EMAIL;
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;

  // Sticky header gets a stronger background after scrolling
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Same offer logic as before (used to offset the header under the offer bar)
  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getoffertop`);
        if (response.data && response.data.length > 0) setOffer(response.data[0]);
      } catch (err) {
        // no offer: header sits at the top
      }
    };
    fetchOffer();
  }, []);

  // Close menus when the page changes
  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Lock page scroll while the mobile menu is open, close it with Escape
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [isMenuOpen]);

  const openDropdown = (key) => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setActiveDropdown(key);
  };
  const closeDropdownSoon = () => {
    leaveTimer.current = setTimeout(() => setActiveDropdown(null), 200);
  };
  const toggleDropdown = (key) => setActiveDropdown(activeDropdown === key ? null : key);

  const isActive = (to) => to && location.pathname.toLowerCase() === to.toLowerCase();
  const groupActive = (items) => items.some((i) => isActive(i.to));

  const whatsappUrl = `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(
    "Hi! I'd like to book a FREE trial class for my child."
  )}&type=phone_number&app_absent=0`;

  return (
    <>
      <header className={`rmh-header ${isScrolled ? 'is-scrolled' : ''} ${offer ? 'rmh-below-offer' : ''}`}>
        <div className="rmh-header-inner">
          <Link to="/" className="rmh-logo" aria-label="RoboMind home">
            <img src="../assets/img/logo/logowhitenew.png" alt="RoboMind Coding Education" />
          </Link>

          {/* Desktop navigation */}
          <nav className="rmh-nav" aria-label="Main">
            <ul className="rmh-nav-list">
              {NAV.map((item) =>
                item.items ? (
                  <li
                    key={item.key}
                    className={`rmh-nav-item has-menu ${activeDropdown === item.key ? 'is-open' : ''}`}
                    onMouseEnter={() => openDropdown(item.key)}
                    onMouseLeave={closeDropdownSoon}
                  >
                    <button
                      type="button"
                      className={`rmh-nav-link ${groupActive(item.items) ? 'is-active' : ''}`}
                      aria-expanded={activeDropdown === item.key}
                      onClick={() => toggleDropdown(item.key)}
                    >
                      {item.label}
                      <FontAwesomeIcon icon={faChevronDown} className="rmh-caret" />
                    </button>
                    <div className="rmh-menu">
                      {item.items.map((sub) => (
                        <Link key={sub.to} to={sub.to} className="rmh-menu-item">
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </li>
                ) : (
                  <li key={item.to} className="rmh-nav-item">
                    <Link to={item.to} className={`rmh-nav-link ${isActive(item.to) ? 'is-active' : ''}`}>
                      {item.label}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </nav>

          <div className="rmh-header-actions">
            {/* Utility links: already-booked trial students (code) and paid students and teachers */}
            <div className="rmh-utility rmh-hide-mobile">
              <Link to="/joinfreeclass" className="rmh-utility-link">
                <FontAwesomeIcon icon={faKey} />
                Join Free Class
              </Link>
              <Link to="/login" className="rmh-utility-link">
                <FontAwesomeIcon icon={faSignInAlt} />
                Login
              </Link>
            </div>
            <button
              type="button"
              className="rmh-burger"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="rmh-mobile-menu"
              onClick={() => setMenuOpen(!isMenuOpen)}
            >
              <FontAwesomeIcon icon={isMenuOpen ? faXmark : faBars} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div className={`rmh-drawer ${isMenuOpen ? 'is-open' : ''}`} aria-hidden={!isMenuOpen}>
        <button
          type="button"
          className="rmh-drawer-backdrop"
          aria-label="Close menu"
          tabIndex={-1}
          onClick={() => setMenuOpen(false)}
        />
        <div className="rmh-drawer-panel" id="rmh-mobile-menu" role="dialog" aria-label="Menu">
          <div className="rmh-drawer-head">
            <img src="../assets/img/logo/logowhitenew.png" alt="RoboMind Coding Education" />
            <button type="button" className="rmh-burger" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <nav aria-label="Mobile">
            <ul className="rmh-drawer-list">
              {NAV.map((item) =>
                item.items ? (
                  <li key={item.key} className={`rmh-drawer-item ${activeDropdown === `m-${item.key}` ? 'is-open' : ''}`}>
                    <button
                      type="button"
                      className="rmh-drawer-link"
                      aria-expanded={activeDropdown === `m-${item.key}`}
                      onClick={() => toggleDropdown(`m-${item.key}`)}
                    >
                      {item.label}
                      <FontAwesomeIcon icon={faChevronDown} className="rmh-caret" />
                    </button>
                    <div className="rmh-drawer-sub">
                      {item.items.map((sub) => (
                        <Link key={sub.to} to={sub.to} className="rmh-drawer-sublink" onClick={() => setMenuOpen(false)}>
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </li>
                ) : (
                  <li key={item.to} className="rmh-drawer-item">
                    <Link
                      to={item.to}
                      className={`rmh-drawer-link ${isActive(item.to) ? 'is-active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </nav>

          <div className="rmh-drawer-actions">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="rmh-btn rmh-btn-ghost rmh-btn-block">
              <FontAwesomeIcon icon={faWhatsapp} />
              Chat on WhatsApp
            </a>
          </div>

          {/* For people who already booked or are enrolled */}
          <div className="rmh-drawer-access">
            <Link to="/joinfreeclass" className="rmh-access-card" onClick={() => setMenuOpen(false)}>
              <FontAwesomeIcon icon={faKey} />
              <span>
                <strong>Join Free Class</strong>
                <small>Already booked? Enter your code</small>
              </span>
            </Link>
            <Link to="/login" className="rmh-access-card" onClick={() => setMenuOpen(false)}>
              <FontAwesomeIcon icon={faSignInAlt} />
              <span>
                <strong>Login</strong>
                <small>Students &amp; teachers</small>
              </span>
            </Link>
          </div>

          <div className="rmh-drawer-contact">
            {EMAIL && (
              <a href={`mailto:${EMAIL}`}>
                <FontAwesomeIcon icon={faEnvelope} />
                {EMAIL}
              </a>
            )}
            {PHONE && (
              <a href={`tel:${PHONE}`}>
                <FontAwesomeIcon icon={faPhoneVolume} />
                {PHONE}
              </a>
            )}
            <span>
              <FontAwesomeIcon icon={faLocationDot} />
              Lebanon · Online worldwide
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;